import { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";

import { EmptyState } from "@lopie/kernel-ui";
import { api } from "../lib/projects-api.ts";
import { initials } from "../lib/initials.ts";
import EpicGantt from "../components/EpicGantt.jsx";
import EpicStatusPill from "../components/EpicStatusPill.jsx";

const COLOR_PALETTE = ["#1f7a3d", "#0b6fb3", "#5b53b8", "#b3730b", "#a32b2b", "#0f7b8a"];
const STATUS_OVERRIDES = ["auto", "on_track", "at_risk", "blocked"];
const STATUS_LABEL = { auto: "Auto", on_track: "On track", at_risk: "At risk", blocked: "Blocked" };

/**
 * The epics as a roadmap — ported from the tasks monolith's Roadmap tab: a
 * Gantt of the epics with dates, and below it one row per epic with its
 * rolled-up progress, expandable to the tasks that make up that rollup.
 *
 * `tasks` is the project's full task list (already loaded for the board), so
 * expanding a row costs nothing: it is a filter, not a fetch.
 */
export default function Roadmap({ project, epics, tasks, people, readOnly = false, onReload }) {
  const [editing, setEditing] = useState(null); // null | {} (new) | epic
  const [selectedId, setSelectedId] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set());

  const peopleById = useMemo(() => new Map(people.map((p) => [p.id, p])), [people]);
  const shown = selectedId ? epics.filter((e) => e.id === selectedId) : epics;

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6">Roadmap</Typography>
          <Typography variant="body2" color="text.secondary">
            Group tasks into the arcs of work over this board.
          </Typography>
        </Box>
        {!readOnly ? <Button variant="contained" onClick={() => setEditing({})}>New epic</Button> : null}
      </Stack>

      {!epics.length ? (
        <EmptyState title="No epics yet"
          detail="Group tasks into features and give them start and target dates to plot them here." />
      ) : (
        <>
          <EpicGantt epics={epics} peopleById={peopleById} fallbackColor={project.color}
            selectedEpicId={selectedId}
            onEpicClick={(e) => setSelectedId((id) => (id === e.id ? null : e.id))}
            emptyMessage="No epics with start/target dates in this window. Edit an epic and set its dates to plot it here." />

          {selectedId ? (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <Typography variant="caption" color="text.secondary">Filtered to one epic</Typography>
              <Button size="small" onClick={() => setSelectedId(null)}>Clear</Button>
            </Stack>
          ) : null}

          <Stack spacing={1}>
            {shown.map((epic) => (
              <EpicRow key={epic.id} epic={epic} owner={peopleById.get(epic.ownerId)}
                tasks={tasks} readOnly={readOnly}
                expanded={expanded.has(epic.id)} onToggle={() => toggle(epic.id)}
                onEdit={() => setEditing(epic)} />
            ))}
          </Stack>
        </>
      )}

      {editing ? (
        <EpicDialog project={project} epic={editing} people={people}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); onReload(); }} />
      ) : null}
    </Stack>
  );
}

function EpicRow({ epic, owner, tasks, readOnly, expanded, onToggle, onEdit }) {
  const rows = useMemo(
    () => tasks.filter((t) => t.epicId === epic.id && !t.parentTaskId),
    [tasks, epic.id]);
  const targetLabel = epic.targetDate
    ? new Date(`${epic.targetDate}T00:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <Paper variant="outlined">
      <Stack direction="row" spacing={1.5} sx={{ p: 1.5, alignItems: "center" }}>
        <IconButton size="small" onClick={onToggle} aria-label={expanded ? "Collapse" : "Expand"}>
          <Box component="span" sx={{ fontSize: 14, lineHeight: 1 }}>{expanded ? "▾" : "▸"}</Box>
        </IconButton>
        <Box sx={{ width: 12, height: 12, borderRadius: "50%", flexShrink: 0, bgcolor: epic.color || "primary.main" }} />
        <Stack sx={{ flex: 1, minWidth: 0 }} spacing={0.5}>
          <Stack direction="row" spacing={1} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Typography variant="subtitle2" noWrap>{epic.name}</Typography>
            <EpicStatusPill status={epic.status} />
          </Stack>
          <Stack direction="row" spacing={1.5} sx={{ alignItems: "center", flexWrap: "wrap" }}>
            <Box sx={{ width: 120 }}>
              <LinearProgress variant="determinate" value={epic.counts?.percentComplete ?? 0}
                sx={{ height: 6, borderRadius: 3 }} />
            </Box>
            <Typography variant="caption" color="text.secondary">
              {epic.counts?.done ?? 0}/{epic.counts?.total ?? 0} shipped
            </Typography>
            {targetLabel ? <Typography variant="caption" color="text.secondary">&middot; Target {targetLabel}</Typography> : null}
            {owner ? (
              <Tooltip title={`Owner: ${owner.displayName || owner.emails?.[0]}`}>
                <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                  <Avatar sx={{ width: 18, height: 18, fontSize: 9 }}>{initials(owner.displayName || owner.emails?.[0])}</Avatar>
                  <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 120 }}>
                    {owner.displayName || owner.emails?.[0]}
                  </Typography>
                </Stack>
              </Tooltip>
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ fontStyle: "italic" }}>&middot; No owner</Typography>
            )}
          </Stack>
        </Stack>
        {!readOnly ? <Button size="small" onClick={onEdit}>Edit</Button> : null}
      </Stack>

      {expanded ? (
        <Box sx={{ borderTop: 1, borderColor: "divider", bgcolor: "action.hover", px: 2, py: 1 }}>
          {!rows.length ? (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic", py: 1 }}>
              No tasks in this epic yet. Open a task and pick this epic in the drawer to add it here.
            </Typography>
          ) : (
            <Stack spacing={0.5}>
              {rows.map((t) => (
                <Stack key={t.id} direction="row" spacing={1} sx={{ alignItems: "center", py: 0.5 }}>
                  <Box component="span" sx={{ width: 6, height: 6, borderRadius: "50%",
                    bgcolor: t.status === "done" ? "success.main" : "action.disabled" }} />
                  <Typography variant="caption" color="text.secondary" sx={{ fontFamily: "monospace" }}>
                    #{t.number}
                  </Typography>
                  <Typography variant="body2" noWrap sx={{ flex: 1 }}>{t.title}</Typography>
                  {t.status === "done" ? (
                    <Typography variant="caption" color="success.main" sx={{ textTransform: "uppercase", fontWeight: 700 }}>
                      Done
                    </Typography>
                  ) : null}
                </Stack>
              ))}
            </Stack>
          )}
        </Box>
      ) : null}
    </Paper>
  );
}

function EpicDialog({ project, epic, people, onClose, onSaved }) {
  const isNew = !epic.id;
  const [name, setName] = useState(epic.name || "");
  const [description, setDescription] = useState(epic.description || "");
  const [color, setColor] = useState(epic.color || COLOR_PALETTE[0]);
  const [startDate, setStartDate] = useState(epic.startDate || "");
  const [targetDate, setTargetDate] = useState(epic.targetDate || "");
  const [ownerId, setOwnerId] = useState(epic.ownerId || "");
  const [statusOverride, setStatusOverride] = useState(epic.statusOverride || "auto");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const body = {
        name, description, color, startDate, targetDate, ownerId,
        statusOverride: statusOverride === "auto" ? "" : statusOverride,
      };
      if (isNew) await api.createEpic(project.id, body);
      else await api.updateEpic(project.id, epic.id, body);
      onSaved();
    } catch (e) {
      setError(e.body?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isNew ? "New epic" : "Edit epic"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} autoFocus fullWidth />
          <TextField label="Description" value={description} onChange={(e) => setDescription(e.target.value)}
            multiline minRows={2} fullWidth />

          <Stack direction="row" spacing={1}>
            {COLOR_PALETTE.map((c) => (
              <Box key={c} component="button" type="button" onClick={() => setColor(c)}
                aria-label={`Color ${c}`}
                sx={{ width: 24, height: 24, borderRadius: "50%", bgcolor: c, cursor: "pointer",
                      border: color === c ? "2px solid" : "2px solid transparent", borderColor: color === c ? "text.primary" : "transparent",
                      p: 0 }} />
            ))}
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField type="date" label="Start date" slotProps={{ inputLabel: { shrink: true } }}
              value={startDate} onChange={(e) => setStartDate(e.target.value)} sx={{ flex: 1 }} />
            <TextField type="date" label="Target date" slotProps={{ inputLabel: { shrink: true } }}
              value={targetDate} onChange={(e) => setTargetDate(e.target.value)} sx={{ flex: 1 }} />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField select label="Owner" value={ownerId} onChange={(e) => setOwnerId(e.target.value)} sx={{ flex: 1 }}>
              <MenuItem value="">No owner</MenuItem>
              {people.map((p) => <MenuItem key={p.id} value={p.id}>{p.displayName || p.emails?.[0] || p.id}</MenuItem>)}
            </TextField>
            <TextField select label="Status" value={statusOverride} onChange={(e) => setStatusOverride(e.target.value)} sx={{ flex: 1 }}>
              {STATUS_OVERRIDES.map((s) => <MenuItem key={s} value={s}>{STATUS_LABEL[s]}</MenuItem>)}
            </TextField>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Auto derives status from tasks shipped and the target date. Anything else is a person's word, and wins.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy || !name.trim()}>
          {isNew ? "Create" : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
